import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../../config";
import { extractErrorMessage } from "../../../utils/apiError";
import m from "./LogVisitModal.module.css";

/** Premium "Log a clinic visit" modal — the nurse picks the student
 *  (search-as-you-type), captures complaint + treatment + free-form
 *  notes, then POSTs to /api/clinic/visits. The list of students is
 *  passed in by the parent so the modal opens instantly (no fetch on
 *  open). On success the parent's onSaved() callback refreshes the
 *  dashboard so the new visit appears in the recent list and the
 *  tile counts tick up. */
export default function LogVisitModal({ open, students, onClose, onSaved }) {
  const [studentId,  setStudentId]  = useState("");
  const [search,     setSearch]     = useState("");
  const [complaint,  setComplaint]  = useState("");
  const [treatment,  setTreatment]  = useState("");
  const [notes,      setNotes]      = useState("");
  const [busy,       setBusy]       = useState(false);
  const [err,        setErr]        = useState("");
  const searchInputRef = useRef(null);

  // Escape closes; body-scroll lock so the page doesn't jitter behind.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === "Escape" && !busy) onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Auto-focus the search when the modal opens so the nurse can
    // start typing immediately.
    setTimeout(() => searchInputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onClose]);

  // Reset form when the modal closes so a second open starts fresh.
  useEffect(() => {
    if (open) return;
    setStudentId(""); setSearch("");
    setComplaint(""); setTreatment(""); setNotes("");
    setErr("");
  }, [open]);

  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students.slice(0, 60);
    return students.filter(st => {
      const full = `${st.firstName || ""} ${st.lastName || ""}`.toLowerCase();
      return full.includes(q)
          || (st.studentNumber || "").toLowerCase().includes(q);
    }).slice(0, 60);
  }, [students, search]);

  const selectedStudent = useMemo(
    () => students.find(s => String(s.id) === String(studentId)) || null,
    [students, studentId]
  );

  async function save(e) {
    e?.preventDefault();
    if (busy) return;
    if (!studentId) { setErr("Pick a student first."); return; }
    if (!complaint.trim() && !treatment.trim() && !notes.trim()) {
      setErr("Add at least a complaint, treatment, or note before saving.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/clinic/visits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId: Number(studentId),
          complaint: complaint.trim() || null,
          treatment: treatment.trim() || null,
          notes:     notes.trim()     || null,
        }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      onSaved?.();
      onClose?.();
    } catch (e2) {
      setErr(e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className={m.backdrop}>
      <div className={m.modal}
           role="dialog"
           aria-modal="true"
           aria-labelledby="logVisitTitle">
        <header className={m.head}>
          <div className={m.headIcon} aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>
            </svg>
          </div>
          <div className={m.headIdent}>
            <div className={m.eyebrow}>Clinic</div>
            <h2 id="logVisitTitle" className={m.title}>Log a clinic visit</h2>
          </div>
          <button type="button"
                  className={m.closeBtn}
                  onClick={onClose}
                  disabled={busy}
                  aria-label="Close">✕</button>
        </header>

        <form className={m.body} onSubmit={save}>
          {/* Student picker — search as you type, click a row to pick. */}
          <div className={m.field}>
            <label className={m.label}>Student <span className={m.req}>*</span></label>
            {selectedStudent ? (
              <div className={m.chosenChip}>
                <div className={m.chosenAvatar}>
                  {((selectedStudent.firstName || "")[0] || "?").toUpperCase()}
                  {((selectedStudent.lastName  || "")[0] || "").toUpperCase()}
                </div>
                <div className={m.chosenBody}>
                  <div className={m.chosenName}>
                    {selectedStudent.lastName}, {selectedStudent.firstName}
                  </div>
                  {selectedStudent.studentNumber && (
                    <div className={m.chosenNumber}>#{selectedStudent.studentNumber}</div>
                  )}
                </div>
                <button type="button"
                        className={m.chosenChange}
                        onClick={() => { setStudentId(""); setTimeout(() => searchInputRef.current?.focus(), 0); }}
                        disabled={busy}>Change</button>
              </div>
            ) : (
              <>
                <div className={m.searchWrap}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                       className={m.searchIcon} aria-hidden="true">
                    <circle cx="9" cy="9" r="6"/>
                    <path d="M15 15l-3.5-3.5"/>
                  </svg>
                  <input
                    ref={searchInputRef}
                    className={m.searchInput}
                    type="text"
                    placeholder="Search by name or student #…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className={m.studentList}>
                  {visibleStudents.length === 0 ? (
                    <div className={m.studentEmpty}>No students match "{search}".</div>
                  ) : (
                    visibleStudents.map(st => (
                      <button
                        key={st.id}
                        type="button"
                        className={m.studentRow}
                        onClick={() => { setStudentId(st.id); setSearch(""); }}>
                        <div className={m.studentRowAvatar}>
                          {((st.firstName || "")[0] || "?").toUpperCase()}
                          {((st.lastName  || "")[0] || "").toUpperCase()}
                        </div>
                        <div className={m.studentRowBody}>
                          <div className={m.studentRowName}>
                            {st.lastName}, {st.firstName}
                          </div>
                          {st.studentNumber && (
                            <div className={m.studentRowNumber}>#{st.studentNumber}</div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Two short text fields side-by-side, then notes textarea below. */}
          <div className={m.fieldPair}>
            <div className={m.field}>
              <label className={m.label}>Complaint</label>
              <input
                type="text"
                className={m.input}
                value={complaint}
                onChange={e => setComplaint(e.target.value)}
                placeholder="e.g. headache, fever, stomachache"
                maxLength={512}
                disabled={busy}
              />
            </div>
            <div className={m.field}>
              <label className={m.label}>Treatment given</label>
              <input
                type="text"
                className={m.input}
                value={treatment}
                onChange={e => setTreatment(e.target.value)}
                placeholder="e.g. paracetamol 250mg, ice pack"
                maxLength={512}
                disabled={busy}
              />
            </div>
          </div>

          <div className={m.field}>
            <label className={m.label}>Notes</label>
            <textarea
              className={m.textarea}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional — observation, parent contacted, sent home, etc."
              rows={3}
              disabled={busy}
            />
          </div>

          {err && <div className={m.error}>{err}</div>}

          <footer className={m.foot}>
            <button type="button"
                    className={m.btnGhost}
                    onClick={onClose}
                    disabled={busy}>Cancel</button>
            <button type="submit"
                    className={m.btnPrimary}
                    disabled={busy || !studentId}>
              {busy ? "Saving…" : "Log visit"}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
}
